import { useEffect, useState } from "react";
import { Modal, Button, Spin, Typography, Space } from "antd";
import { DownloadOutlined, CloseOutlined } from "@ant-design/icons";
import mammoth from "mammoth/mammoth.browser";
import { SupervisedItem } from "@/types";
import { FileIcon } from "@/components/shared";
import { formatFileSize } from "@/utils/formatters";

const { Text, Title } = Typography;

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

interface Props {
  item: SupervisedItem | null;
  blob: Blob | null;
  open: boolean;
  onClose: () => void;
  onDownload: (item: SupervisedItem) => void;
}

/**
 * Preview modal for the supervision view. Renders the already-downloaded blob
 * fully client-side (no third-party services):
 *  - images / pdf  -> object URL
 *  - .docx         -> mammoth (docx -> HTML, in-browser)
 *  - text          -> plain text
 *  - everything else -> download fallback
 */
export default function SupervisionPreviewModal({
  item,
  blob,
  open,
  onClose,
  onDownload,
}: Props) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [docHtml, setDocHtml] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mime = item?.mimeType || blob?.type || "";
  const name = item?.name || "";
  const isImage = mime.startsWith("image/");
  const isPdf = mime === "application/pdf";
  const isDocx = mime === DOCX_MIME || name.toLowerCase().endsWith(".docx");
  const isText = mime.startsWith("text/");

  useEffect(() => {
    let createdUrl: string | null = null;
    setDocHtml(null);
    setTextContent(null);
    setObjectUrl(null);
    setError(null);

    if (!blob || !item) return;

    setLoading(true);
    (async () => {
      try {
        if (isImage || isPdf) {
          createdUrl = URL.createObjectURL(blob);
          setObjectUrl(createdUrl);
        } else if (isDocx) {
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setDocHtml(result.value || "<p><em>(empty document)</em></p>");
        } else if (isText) {
          setTextContent(await blob.text());
        }
      } catch {
        setError("Could not render a preview for this file.");
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob, item]);

  const renderBody = () => {
    if (loading) {
      return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
          <Spin size="large" />
        </div>
      );
    }

    if (!error && isImage && objectUrl) {
      return (
        <div style={{ display: "flex", justifyContent: "center", background: "#f5f5f5", minHeight: 400 }}>
          <img src={objectUrl} alt={name} style={{ maxHeight: "70vh", objectFit: "contain" }} />
        </div>
      );
    }

    if (!error && isPdf && objectUrl) {
      return <iframe src={objectUrl} title={name} style={{ width: "100%", height: "75vh", border: "none" }} />;
    }

    if (!error && isDocx && docHtml !== null) {
      return (
        <div
          style={{
            maxHeight: "75vh",
            overflow: "auto",
            padding: "32px 48px",
            background: "#fff",
            lineHeight: 1.6,
          }}
          dangerouslySetInnerHTML={{ __html: docHtml }}
        />
      );
    }

    if (!error && isText && textContent !== null) {
      return (
        <pre style={{ maxHeight: "75vh", overflow: "auto", padding: 24, margin: 0, whiteSpace: "pre-wrap" }}>
          {textContent}
        </pre>
      );
    }

    // Fallback (unsupported type or render error)
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
          padding: 48,
        }}
      >
        {item && <FileIcon mimeType={item.mimeType || ""} size="large" />}
        <Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
          {name}
        </Title>
        <Text type="secondary" style={{ marginBottom: 24 }}>
          {error || "Preview not available for this file type"}
          {item?.size ? ` · ${formatFileSize(item.size)}` : ""}
        </Text>
        {item && (
          <Button type="primary" icon={<DownloadOutlined />} onClick={() => onDownload(item)}>
            Download file
          </Button>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="90vw"
      style={{ top: 20 }}
      styles={{ body: { padding: 0 } }}
      closeIcon={<CloseOutlined />}
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 40 }}>
          <Text strong ellipsis style={{ maxWidth: 500 }}>
            {name}
          </Text>
          {item && (
            <Space>
              <Button icon={<DownloadOutlined />} onClick={() => onDownload(item)}>
                Download
              </Button>
            </Space>
          )}
        </div>
      }
    >
      {renderBody()}
    </Modal>
  );
}
